import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainer } from '@/components/calculator/FieldExplainer';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { Select } from '@/components/ui/select';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getEmergencyFundFieldExplainers } from '@/content/field-explainers/emergency-fund';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateEmergencyFund, type EmergencyFundInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  parseContractState
} from './shared';

type EmergencyState = EmergencyFundInput;

const defaults: EmergencyState = {
  expenses: {
    housing: 1500,
    utilities: 220,
    groceries: 420,
    transportation: 320,
    insurance: 280,
    debtPayments: 240,
    other: 180
  },
  currentSavings: 7000,
  monthlyContribution: 550,
  employment: 'stable',
  incomeSources: 'single',
  dependents: 'none',
  jobMarket: 'good'
};

const chartConfig = {
  savings: {
    label: 'Savings',
    color: 'var(--chart-1)'
  },
  target: {
    label: 'Target line',
    color: 'var(--chart-4)'
  }
} satisfies ChartConfig;

type Props = { route: RouteDefinition };

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

export function EmergencyFundCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<EmergencyState>(() => parseContractState('emergencyFund', defaults));

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result = useMemo(() => calculateEmergencyFund(state), [state]);

  const explainers = useMemo(() => {
    const entries = getEmergencyFundFieldExplainers({
      input: {
        housing: state.expenses.housing,
        utilities: state.expenses.utilities,
        groceries: state.expenses.groceries,
        transportation: state.expenses.transportation,
        insurance: state.expenses.insurance,
        debtPayments: state.expenses.debtPayments,
        other: state.expenses.other,
        currentSavings: state.currentSavings,
        monthlyContribution: state.monthlyContribution,
        employment: state.employment,
        incomeSources: state.incomeSources,
        dependents: state.dependents,
        jobMarket: state.jobMarket
      },
      result: {
        totalMonthlyExpenses: result.totalMonthlyExpenses,
        recommendedMonths: result.recommendedMonths,
        targetEmergencyFund: result.targetEmergencyFund,
        currentMonthsCovered: result.currentMonthsCovered,
        progressPercent: result.progressPercent,
        timeToGoal: result.timeToGoal
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [state, result]);

  const chartData = result.chart.labels.map((label, index) => ({
    month: label,
    savings: result.chart.savings[index],
    target: result.chart.targetLine[index]
  }));

  const expenseRows = Object.entries(state.expenses).map(([category, amount]) => ({ category, amount }));

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Emergency Fund Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Size your emergency reserve target and track monthly progress against recommended coverage.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel title="Monthly expenses and risk factors" description="Enter expense obligations and stability factors used by recommendation logic.">
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="efHousing" label="Housing" value={state.expenses.housing} min={0} step={25} prefix="$" explainer={explainers.byId.get('housing')} onChange={(value) => setState((prev) => ({ ...prev, expenses: { ...prev.expenses, housing: value } }))} />
            <NumberField id="efUtilities" label="Utilities" value={state.expenses.utilities} min={0} step={10} prefix="$" explainer={explainers.byId.get('utilities')} onChange={(value) => setState((prev) => ({ ...prev, expenses: { ...prev.expenses, utilities: value } }))} />
            <NumberField id="efGroceries" label="Groceries" value={state.expenses.groceries} min={0} step={10} prefix="$" explainer={explainers.byId.get('groceries')} onChange={(value) => setState((prev) => ({ ...prev, expenses: { ...prev.expenses, groceries: value } }))} />
            <NumberField id="efTransportation" label="Transportation" value={state.expenses.transportation} min={0} step={10} prefix="$" explainer={explainers.byId.get('transportation')} onChange={(value) => setState((prev) => ({ ...prev, expenses: { ...prev.expenses, transportation: value } }))} />
            <NumberField id="efInsurance" label="Insurance" value={state.expenses.insurance} min={0} step={10} prefix="$" explainer={explainers.byId.get('insurance')} onChange={(value) => setState((prev) => ({ ...prev, expenses: { ...prev.expenses, insurance: value } }))} />
            <NumberField id="efDebtPayments" label="Debt payments" value={state.expenses.debtPayments} min={0} step={10} prefix="$" explainer={explainers.byId.get('debtPayments')} onChange={(value) => setState((prev) => ({ ...prev, expenses: { ...prev.expenses, debtPayments: value } }))} />
            <NumberField id="efOther" label="Other essentials" value={state.expenses.other} min={0} step={10} prefix="$" explainer={explainers.byId.get('other')} onChange={(value) => setState((prev) => ({ ...prev, expenses: { ...prev.expenses, other: value } }))} />
            <NumberField id="efCurrentSavings" label="Current savings" value={state.currentSavings} min={0} step={100} prefix="$" explainer={explainers.byId.get('currentSavings')} onChange={(value) => setState((prev) => ({ ...prev, currentSavings: value }))} />
            <NumberField id="efMonthlyContribution" label="Monthly contribution" value={state.monthlyContribution} min={0} step={25} prefix="$" explainer={explainers.byId.get('monthlyContribution')} onChange={(value) => setState((prev) => ({ ...prev, monthlyContribution: value }))} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm" htmlFor="emergency-employment">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                Employment stability
                {explainers.byId.get('employment') ? <FieldExplainer explainer={explainers.byId.get('employment')!} /> : null}
              </span>
              <Select id="emergency-employment" value={state.employment} onChange={(event) => setState((prev) => ({ ...prev, employment: event.target.value as EmergencyFundInput['employment'] }))} options={[{ value: 'stable', label: 'Stable salaried' }, { value: 'contract', label: 'Contract' }, { value: 'self', label: 'Self-employed' }, { value: 'variable', label: 'Variable income' }]} />
            </label>
            <label className="space-y-2 text-sm" htmlFor="emergency-income-sources">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                Income sources
                {explainers.byId.get('incomeSources') ? <FieldExplainer explainer={explainers.byId.get('incomeSources')!} /> : null}
              </span>
              <Select id="emergency-income-sources" value={state.incomeSources} onChange={(event) => setState((prev) => ({ ...prev, incomeSources: event.target.value as EmergencyFundInput['incomeSources'] }))} options={[{ value: 'single', label: 'Single source' }, { value: 'dual', label: 'Dual income' }, { value: 'multiple', label: 'Multiple sources' }]} />
            </label>
            <label className="space-y-2 text-sm" htmlFor="emergency-dependents">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                Dependents
                {explainers.byId.get('dependents') ? <FieldExplainer explainer={explainers.byId.get('dependents')!} /> : null}
              </span>
              <Select id="emergency-dependents" value={state.dependents} onChange={(event) => setState((prev) => ({ ...prev, dependents: event.target.value as EmergencyFundInput['dependents'] }))} options={[{ value: 'none', label: 'None' }, { value: 'some', label: 'Some' }, { value: 'many', label: 'Many' }]} />
            </label>
            <label className="space-y-2 text-sm" htmlFor="emergency-job-market">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                Job market
                {explainers.byId.get('jobMarket') ? <FieldExplainer explainer={explainers.byId.get('jobMarket')!} /> : null}
              </span>
              <Select id="emergency-job-market" value={state.jobMarket} onChange={(event) => setState((prev) => ({ ...prev, jobMarket: event.target.value as EmergencyFundInput['jobMarket'] }))} options={[{ value: 'hot', label: 'Hot market' }, { value: 'good', label: 'Good market' }, { value: 'tough', label: 'Tough market' }]} />
            </label>
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Recommended fund" value={formatCurrency(result.targetEmergencyFund)} note={`${result.recommendedMonths} months of expenses`} trend="flat" explainer={explainers.byId.get('targetEmergencyFund')} />
          <ResultSummaryCard label="Progress" value={`${result.progressPercent}%`} note={`current coverage: ${result.currentMonthsCovered.toFixed(1)} months`} trend={result.progressPercent >= 100 ? 'up' : 'flat'} explainer={explainers.byId.get('progressPercent')} />
          <ResultSummaryCard label="Time to goal" value={result.timeToGoal} note="based on current monthly contribution" trend={result.progressPercent >= 100 ? 'up' : 'down'} explainer={explainers.byId.get('timeToGoal')} />
          <ResultSummaryCard label="Monthly expenses" value={formatCurrency(result.totalMonthlyExpenses)} note="sum of all essential expense categories" trend="flat" explainer={explainers.byId.get('totalMonthlyExpenses')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'emergencyFund', state as Record<string, unknown>);
              const ok = await copyToClipboard(shareUrl);
              toast({ title: ok ? 'Share link copied' : 'Share link ready', description: ok ? shareUrl : `Copy manually: ${shareUrl}`, variant: ok ? 'success' : 'warning' });
            }}
            onExportCsv={() => {
              const headers = ['Month', 'Savings', 'Target'];
              const rows = chartData.map((row) => [row.month, Math.round(row.savings), Math.round(row.target)]);
              exportRowsToCsv(headers, rows, 'emergency-fund-timeline.csv');
              toast({ title: 'CSV exported', description: 'emergency-fund-timeline.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'Emergency Fund Projection',
                subtitle: `${result.recommendedMonths} month recommended coverage`,
                summaryLeft: [
                  `Monthly expenses: ${formatCurrency(result.totalMonthlyExpenses)}`,
                  `Current savings: ${formatCurrency(state.currentSavings)}`,
                  `Monthly contribution: ${formatCurrency(state.monthlyContribution)}`,
                  `Recommended months: ${result.recommendedMonths}`
                ],
                summaryRight: [
                  `Target fund: ${formatCurrency(result.targetEmergencyFund)}`,
                  `Progress: ${result.progressPercent}%`,
                  `Current months covered: ${result.currentMonthsCovered.toFixed(1)}`,
                  `Time to goal: ${result.timeToGoal}`
                ],
                headers: ['Month', 'Savings', 'Target'],
                rows: chartData.map((row) => [row.month, formatCurrency(row.savings), formatCurrency(row.target)]),
                filename: 'emergency-fund-timeline.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'emergency-fund-timeline.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Savings trajectory"
          description="Full month-by-month savings path compared to your emergency fund target."
          annotation={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>
                Time to goal <span className="font-semibold text-foreground">{result.timeToGoal}</span>
              </span>
              <span>
                Months covered <span className="font-semibold text-foreground">{result.currentMonthsCovered.toFixed(1)}</span>
              </span>
            </div>
          }
          legend={<ChartLegendPills config={chartConfig} keys={['savings', 'target']} />}
        >
          <ChartContainer config={chartConfig} family="comparison">
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{
                top: 18,
                left: 12,
                right: 12
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} width={70} tickFormatter={(value) => formatCompactCurrency(Number(value))} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
              <Line dataKey="savings" type="natural" stroke="var(--color-savings)" strokeWidth={3.1} dot={false} />
              <Line dataKey="target" type="natural" stroke="var(--color-target)" strokeWidth={1.9} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Expense breakdown" description="Current monthly expense categories used for target calculation.">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead numeric>Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenseRows.map((row) => (
                <TableRow key={row.category}>
                  <TableCell>{row.category}</TableCell>
                  <TableCell numeric>{formatCurrency(row.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <ChartCard title="Monthly timeline" description="Complete monthly savings and target values.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Month</TableHead>
                <TableHead numeric>Savings</TableHead>
                <TableHead numeric>Target</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chartData.map((row) => (
                <TableRow key={row.month}>
                  <TableCell numeric>{row.month}</TableCell>
                  <TableCell numeric>{formatCurrency(row.savings)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.target)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="Every input and result on this page, explained with your numbers filled in. Tap the info icon next to any field for the same explanation in place."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
